'use client';

import { breakout } from '@/styles';
import { css, cx } from '@onbeam/business-styled-system/css';
import {
  center,
  flex,
  text,
  vstack,
} from '@onbeam/business-styled-system/patterns';
import { token } from '@onbeam/business-styled-system/tokens';
import { Button } from '@onbeam/business-ui';
import { useBreakpoint } from '@onbeam/utils';
import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { useHeroScrollAnimation } from './useHeroScrollAnimation';
import { Dimension, FINAL_SCALE, BUTTONS_TRIGGER } from './Hero.utils';
import { HeroShader } from './HeroShader';
import NextLink from 'next/link';

const ANIMATION_STORAGE_KEY = 'hasPlayedHeroAnimation';

export const Hero = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollProgress = useHeroScrollAnimation((state) => state.progress);
  const [maxSize, setMaxSize] = useState<Dimension>({ x: 1.5, y: 1.5 });
  const isMobile = useBreakpoint({
    max: token('breakpoints.md'),
  });
  const animationCompelete = useRef<boolean>(false);

  useEffect(() => {
    const hasPlayedAnimation =
      localStorage.getItem(ANIMATION_STORAGE_KEY) === 'true';

    const handleScroll = () => {
      if (!containerRef.current || animationCompelete.current) return;
      const { height, top } = containerRef.current.getBoundingClientRect();
      const newProgress = Math.min(
        Math.max(top / (window.innerHeight - height), 0),
        1,
      );
      useHeroScrollAnimation.setState({ progress: newProgress });
      if (newProgress < 1) return;
      window.scrollTo({
        top: 0,
        behavior: 'instant',
      });
      animationCompelete.current = true;
      localStorage.setItem(ANIMATION_STORAGE_KEY, 'true');
      window.removeEventListener('resize', handleScroll);
      window.removeEventListener('scroll', handleScroll);
    };

    const handleResize = () => {
      handleScroll();
      if (!contentRef.current) return;
      setMaxSize({
        x: window.innerWidth / contentRef.current.clientWidth,
        y: window.innerHeight / contentRef.current.clientHeight,
      });
    };

    if (!hasPlayedAnimation) {
      handleResize();
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', handleResize);
    } else {
      useHeroScrollAnimation.setState({ progress: 1 });
    }
    return () => {
      useHeroScrollAnimation.setState({ progress: 0 });
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const initialScale = Math.max(maxSize.x, maxSize.y) * 2 - 1;
  const scale = initialScale - scrollProgress * (initialScale - FINAL_SCALE);

  return (
    <section
      ref={containerRef}
      data-finished={scrollProgress >= 1}
      className={cx(
        'light',
        breakout(),
        css({
          mdDown: {
            px: '6',
          },
          '&[data-finished="false"]': {
            h: '[140vh]',
            mt: '[-140vh]',
          },
        }),
      )}
    >
      <div
        ref={contentRef}
        data-finished={scrollProgress >= 1}
        className={center({
          position: 'sticky',
          mx: 'auto',
          maxW: '[85rem]',
          w: 'full',
          aspectRatio: { base: '[398/840]', md: '[1360/808]' },
          // Subtract pb and header height
          maxH: '[calc(100vh - 1.5rem - 4.75rem)]',
          top: '[4.75rem]',
          xl: {
            // Subtract py and header height
            maxH: '[calc(100vh - 2rem - 6.25rem)]',
            top: '[6.25rem]',
          },
          '&[data-finished="true"]': {
            position: 'relative',
            top: '0',
          },
        })}
      >
        <div
          className={css({
            position: 'absolute',
            inset: '0',
            rounded: 'md',
            overflow: 'hidden',
          })}
          style={{
            transform: `matrix(${scale}, 0, 0, ${scale}, 0, 0)`,
          }}
        >
          <HeroShader />
        </div>
        <div
          className={vstack({
            align: 'center',
            gap: '5',
            zIndex: '5',
            textAlign: 'center',
            px: '2',
          })}
        >
          <motion.div
            initial={{
              opacity: 0,
            }}
            animate={
              scrollProgress >= BUTTONS_TRIGGER && {
                opacity: 1,
              }
            }
            transition={{
              duration: 0.5,
            }}
            className={css({
              textStyle: { base: 'body-md', md: 'body-xl' },
              color: 'mono.primaryFg',
              px: '4',
              pt: '1.5',
              pb: '1',
              bg: 'mono.90/20',
              rounded: 'full',
              backdropFilter: '[blur(12px)]',
            })}
            style={{
              backdropFilter: 'blur(12px)',
            }}
          >
            Beam Foundation Grants Program
          </motion.div>
          <h1
            className={text({
              style: { base: 'subtitle-lg', md: 'headline-lg' },
              fontSize: { base: 'min(6vw, 2.5rem)', md: 'min(5.5vw, 4rem)' },
              color: 'mono.primaryFg',
            })}
          >
            <motion.span
              initial={{
                opacity: 0,
                y: 25,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.5,
              }}
              className={css({
                display: 'inline-flex',
              })}
            >
              Dream it, build it on Beam.
            </motion.span>
          </h1>
          <motion.div
            initial={{
              opacity: 0,
            }}
            animate={
              scrollProgress >= BUTTONS_TRIGGER && {
                opacity: 1,
              }
            }
            transition={{
              duration: 0.5,
            }}
            className={flex({
              gap: { base: '3', md: '4' },
              mt: '3',
              whiteSpace: 'nowrap',
            })}
          >
            <Button
              size={isMobile ? 'fullWidth' : 'lg'}
              as={NextLink}
              href="/apply"
            >
              Apply now
            </Button>
            {/* <Button
              size={isMobile ? 'fullWidth' : 'lg'}
              as={NextLink}
              href="/bounties"
              variant="secondary"
            >
              See bounties
            </Button> */}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
